import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useHermes } from "./HermesProvider";

/** localStorage 键：Hermes FAB 视口坐标（left/top，px） */
export const HERMES_FAB_POSITION_KEY = "c456.hermes.fab.position";

const SAFE_MARGIN = 24; // 与 bottom-6 / right-6（1.5rem）对齐
const FAB_SIZE = 56; // size-14
const DRAG_THRESHOLD_PX = 5; // 小于此位移视为点击，不打开误触

type FabPosition = { x: number; y: number };

/** 默认落在视口右下角（含安全边距） */
function defaultPosition(): FabPosition {
  return {
    x: window.innerWidth - SAFE_MARGIN - FAB_SIZE,
    y: window.innerHeight - SAFE_MARGIN - FAB_SIZE,
  };
}

/** 将坐标钳制在视口内（含安全边距） */
function clampPosition(pos: FabPosition): FabPosition {
  const maxX = Math.max(SAFE_MARGIN, window.innerWidth - SAFE_MARGIN - FAB_SIZE);
  const maxY = Math.max(SAFE_MARGIN, window.innerHeight - SAFE_MARGIN - FAB_SIZE);
  return {
    x: Math.min(Math.max(SAFE_MARGIN, pos.x), maxX),
    y: Math.min(Math.max(SAFE_MARGIN, pos.y), maxY),
  };
}

function loadPosition(): FabPosition {
  try {
    const raw = localStorage.getItem(HERMES_FAB_POSITION_KEY);
    if (!raw) return defaultPosition();
    const parsed = JSON.parse(raw) as Partial<FabPosition>;
    if (typeof parsed.x !== "number" || typeof parsed.y !== "number") {
      return defaultPosition();
    }
    return clampPosition({ x: parsed.x, y: parsed.y });
  } catch {
    return defaultPosition();
  }
}

function savePosition(pos: FabPosition) {
  try {
    localStorage.setItem(HERMES_FAB_POSITION_KEY, JSON.stringify(pos));
  } catch {
    // 隐私模式 / 配额满时忽略
  }
}

/**
 * 关闭态打开 Hermes 的入口；展开后由抽屉 header 关闭。
 * 自身 fixed 定位，可拖动；单击（无明显拖动）才打开。
 *
 * 拖动跟手：pointermove 只改 DOM transform（translate3d），不 setState；
 * 松手再写 React state + localStorage，避免每帧重渲染与 transition 追赶。
 */
export function HermesFab({ className }: { className?: string }) {
  const { setOpen, brandSiteStudioActive } = useHermes();
  const [position, setPosition] = useState<FabPosition>(defaultPosition);
  const [dragging, setDragging] = useState(false);

  const buttonRef = useRef<HTMLButtonElement>(null);
  // 与 React state 同步，供 pointerdown 取原点（避免闭包陈旧）
  const positionRef = useRef(position);
  positionRef.current = position;

  // 拖动会话：指针起点 + FAB 原点；moved 区分拖动与点击
  const dragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    originX: number;
    originY: number;
    lastX: number;
    lastY: number;
    moved: boolean;
  } | null>(null);
  // click 在 pointerup 之后触发，用此标记抑制拖动后的误开
  const suppressClickRef = useRef(false);

  /** 清除拖动偏移（松手或取消后 left/top 已是最终坐标） */
  const clearDragTransform = () => {
    const el = buttonRef.current;
    if (!el) return;
    el.style.transform = "";
    el.style.willChange = "";
  };

  /** 拖动中：相对原点的 translate3d，走合成层，不触发 React 重渲染 */
  const applyDragTransform = (next: FabPosition, originX: number, originY: number) => {
    const el = buttonRef.current;
    if (!el) return;
    const tx = next.x - originX;
    const ty = next.y - originY;
    el.style.willChange = "transform";
    el.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
  };

  // 挂载后从 localStorage 恢复（并钳制），避免首屏用错旧视口尺寸
  useEffect(() => {
    setPosition(loadPosition());
  }, []);

  // 窗口 resize 时若越界则钳制回视口
  useEffect(() => {
    const onResize = () => {
      setPosition((prev) => {
        const next = clampPosition(prev);
        if (next.x !== prev.x || next.y !== prev.y) {
          savePosition(next);
        }
        return next;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const endDrag = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;

    dragRef.current = null;

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }

    if (drag.moved) {
      suppressClickRef.current = true;
      const next = clampPosition({ x: drag.lastX, y: drag.lastY });
      // 先落 left/top 再清 transform，避免松手瞬间闪回原点
      const el = buttonRef.current;
      if (el) {
        el.style.left = `${next.x}px`;
        el.style.top = `${next.y}px`;
      }
      clearDragTransform();
      setPosition(next);
      savePosition(next);
      setDragging(false);
    } else {
      clearDragTransform();
      setDragging(false);
    }
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    // 仅主指针（鼠标左键 / 触摸主触点）
    if (e.button !== 0) return;
    const origin = positionRef.current;
    suppressClickRef.current = false;
    dragRef.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      originX: origin.x,
      originY: origin.y,
      lastX: origin.x,
      lastY: origin.y,
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;

    const dx = e.clientX - drag.startClientX;
    const dy = e.clientY - drag.startClientY;

    // 未超过阈值：仍视为潜在点击
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;

    const next = clampPosition({
      x: drag.originX + dx,
      y: drag.originY + dy,
    });
    drag.lastX = next.x;
    drag.lastY = next.y;

    // 仅首次越过阈值时进 React（改光标 / 关 transition），之后纯 DOM
    if (!drag.moved) {
      drag.moved = true;
      setDragging(true);
    }

    applyDragTransform(next, drag.originX, drag.originY);
  };

  const onClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    setOpen(true);
  };

  if (brandSiteStudioActive) return null;

  return (
    <Button
      ref={buttonRef}
      type="button"
      size="icon"
      aria-label="打开 Hermes 助手"
      className={cn(
        "fixed z-40 size-14 rounded-full shadow-lg touch-none",
        // Button 默认 transition-all / active:translate-y-px：拖动时必须关掉，否则位置与 transform 会「追赶」指针
        dragging && "cursor-grabbing transition-none active:!translate-y-0",
        !dragging && "cursor-grab",
        className,
      )}
      style={{ left: position.x, top: position.y }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={onClick}
    >
      <Bot className="size-6" />
    </Button>
  );
}
